import type { SupabaseClient } from '@supabase/supabase-js';
import {
  DEFAULT_PRODUCT_TITLE,
  ROADS_TILES_DEFAULT,
  roadsTiles,
  type RoadsTiles,
  isDefaultProductTitle,
  tenantLogoCss,
  tenantLogoSize,
  type TenantLogoSize,
} from '@teslyn/domain';
import { readConfig, type WebConfig } from '../config.ts';
import { setRoadsTiles } from '../map/roads.ts';
import { bundledTenant } from './bundled.ts';
import { resolveTenantSlug, type TenantSource } from './resolve.ts';
import { applyTheme, hasOverrides, sanitiseOverrides, type ThemeOverrides } from './theme.ts';

export interface Tenant {
  /** Database id; null until the tenant row is readable (after sign-in). */
  readonly id: string | null;
  readonly slug: string;
  readonly name: string;
  /** Staff workspace, sign-in and anything shown before the user's area is known, e.g. "Acme Airborne Portal". */
  readonly productTitle: string;
  /** Client area, e.g. "Acme Airborne Client Portal" (MB, 18 Oct 2026). */
  readonly clientTitle: string;
  readonly theme: ThemeOverrides;
  readonly logo: string | null;
  /** Browser-tab icon (a square mark); null keeps Teslyn's favicon. */
  readonly favicon: string | null;
  /** How tall the logo stands in the header and on the sign-in card (decision 524): the tenant admin's choice. */
  readonly logoSize: TenantLogoSize;
  /** N19: the Roads map's tile address the organisation set, else OpenStreetMap's (signed out: always OpenStreetMap's). */
  readonly roads?: RoadsTiles;
  readonly source: TenantSource;
}

export type TenantResult = { readonly kind: 'ok'; readonly tenant: Tenant } | { readonly kind: 'unknown' };

interface TenantRow {
  /** Absent in `tenant_public`'s answer to a signed-out visitor. */
  readonly id?: string | null;
  readonly slug: string;
  readonly name: string;
  readonly product_title: string | null;
  readonly theme: unknown;
}

/**
 * Read the tenant row by slug. Under RLS this returns a row only to signed-in members; a signed-out visitor gets the
 * public branding (name, title, theme: colours and logo size) from `tenant_public`, so the sign-in card shows the
 * tenant admin's logo size. Errors count as "no row".
 */
export async function fetchTenantRow(sb: SupabaseClient, slug: string): Promise<TenantRow | null> {
  try {
    const { data, error } = await sb
      .from('tenant')
      .select('id, slug, name, product_title, theme')
      .eq('slug', slug)
      .maybeSingle();
    if (!error && data) return data as TenantRow;
    const pub = await sb.rpc('tenant_public', { p_slug: slug });
    const row = (Array.isArray(pub.data) ? pub.data[0] : pub.data) as TenantRow | null | undefined;
    return !pub.error && row && typeof row.name === 'string' ? { ...row, id: null } : null;
  } catch {
    return null;
  }
}

/**
 * Merge the DB row over the bundled copy: the DB theme wins when it has any allowed override. A slug with neither
 * (a tenant onboarded after this build) gets Teslyn's own look until its row is readable after sign-in.
 */
export function mergeTenant(slug: string, source: TenantSource, row: TenantRow | null): Tenant {
  const bundled = bundledTenant(slug);
  const dbTheme = sanitiseOverrides(row?.theme);
  const theme = hasOverrides(dbTheme) ? dbTheme : sanitiseOverrides(bundled?.theme);
  const name = row?.name ?? bundled?.name ?? slug;
  return {
    id: row?.id ?? null,
    slug,
    name,
    productTitle:
      row?.product_title && !isDefaultProductTitle(row.product_title)
        ? row.product_title
        : (bundled?.productTitle ?? DEFAULT_PRODUCT_TITLE),
    clientTitle: bundled?.clientTitle ?? `${name} Client Portal`,
    theme,
    logo: bundled?.logoLight ?? null,
    favicon: bundled?.favicon ?? null,
    logoSize: tenantLogoSize(row?.theme, { logo_size: bundled?.logoSize }),
    roads: roadsTiles(row?.theme),
    source,
  };
}

/**
 * Resolve the tenant for this address and paint its theme. Called before the first React render. The bundled theme
 * is applied synchronously first, so a slow or failing database lookup never shows Teslyn colours on a tenant portal.
 */
export async function bootstrapTenant(
  sb: SupabaseClient | null,
  loc: Pick<Location, 'hostname' | 'search'> = window.location,
  cfg: WebConfig = readConfig(),
  doc: Document = document,
  lookupMs = 2000,
): Promise<TenantResult> {
  const resolved = resolveTenantSlug(loc.hostname, loc.search, cfg);
  if (!resolved) return { kind: 'unknown' };
  applyThemeAndTitle(mergeTenant(resolved.slug, resolved.source, null), doc);
  const row = sb ? await withTimeout(fetchTenantRow(sb, resolved.slug), lookupMs) : null;
  const tenant = mergeTenant(resolved.slug, resolved.source, row);
  applyThemeAndTitle(tenant, doc);
  return { kind: 'ok', tenant };
}

function withTimeout<T>(p: Promise<T | null>, ms: number): Promise<T | null> {
  return Promise.race([p, new Promise<null>((resolve) => setTimeout(() => resolve(null), ms))]);
}

export function applyThemeAndTitle(t: Tenant, doc: Document = document): void {
  applyTheme(t.theme, doc);
  setRoadsTiles(t.roads ?? ROADS_TILES_DEFAULT);
  applyLogoSize(t.logoSize, doc);
  doc.title = t.productTitle;
  applyFavicon(t.favicon, doc);
}

export const LOGO_STYLE_ID = 'teslyn-tenant-logo';

/** Set `--tenant-logo-h`, which the header's and the sign-in card's logo read (decision 524). */
export function applyLogoSize(size: TenantLogoSize, doc: Document = document): void {
  let el = doc.getElementById(LOGO_STYLE_ID) as HTMLStyleElement | null;
  if (!el) {
    el = doc.createElement('style');
    el.id = LOGO_STYLE_ID;
    doc.head.appendChild(el);
  }
  el.textContent = tenantLogoCss(size);
}

/**
 * The tenant's square mark in the browser tab (white-label: the tenant's brand leads); Teslyn's otherwise. index.html
 * declares Teslyn's SVG and `.ico` (brand/README.md): a tenant's mark replaces both, so the browser cannot fall back
 * to Teslyn's, and each comes back when there is none.
 */
export function applyFavicon(href: string | null, doc: Document = document): void {
  let links = [...doc.querySelectorAll<HTMLLinkElement>('link[rel="icon"]')];
  if (links.length === 0) {
    const link = doc.createElement('link');
    link.rel = 'icon';
    link.type = 'image/svg+xml';
    link.setAttribute('href', '/favicon.svg');
    doc.head.appendChild(link);
    links = [link];
  }
  for (const link of links) {
    link.dataset['teslynHref'] ??= link.getAttribute('href') ?? '/favicon.svg';
    link.dataset['teslynType'] ??= link.getAttribute('type') ?? '';
    if (href) {
      link.type = href.endsWith('.svg') ? 'image/svg+xml' : 'image/png';
      link.href = href;
    } else {
      link.href = link.dataset['teslynHref'] ?? '/favicon.svg';
      if (link.dataset['teslynType']) link.type = link.dataset['teslynType'];
      else link.removeAttribute('type');
    }
  }
}
